import { useEffect, useState } from 'react';
import { Send, Save, Mail, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { supabase } from '../lib/supabase';

const Settings = () => {
  const [companies, setCompanies] = useState([]);
  const [companyDrafts, setCompanyDrafts] = useState({});
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [followupWebhook, setFollowupWebhook] = useState('');
  const [gmailStatus, setGmailStatus] = useState({ connected: false });
  const [gmailAliases, setGmailAliases] = useState([]);
  const [aliasesError, setAliasesError] = useState('');
  const [followupRules, setFollowupRules] = useState({
    schedules: [
      { days: 3, channels: ['email'] },
      { days: 7, channels: ['email', 'whatsapp'] },
      { days: 15, channels: ['email', 'whatsapp'] },
      { days: 30, channels: ['email', 'whatsapp'] },
    ]
  });
  const [followupTemplates, setFollowupTemplates] = useState({
    email_subject: 'Reminder: Invoice {{invoice_no}} is {{days_overdue}} days overdue',
    email_body: 'Dear {{client_name}},\n\nThis is a reminder that invoice {{invoice_no}} dated {{invoice_date}} for {{balance_due}} is now {{days_overdue}} days past its due date ({{due_date}}).\n\nPlease arrange for payment at the earliest. Reach out if you have any questions.\n\nRegards,\n{{company_name}}',
    whatsapp_body: 'Hi {{client_name}}, a quick reminder — invoice {{invoice_no}} ({{balance_due}}) is {{days_overdue}} days overdue (due {{due_date}}). Could you please look into it? Thanks — {{company_name}}',
  });
  const [savedMsg, setSavedMsg] = useState('');
  const [analystEmails, setAnalystEmails] = useState([]);
  const [newAnalyst, setNewAnalyst] = useState('');
  const [analystMsg, setAnalystMsg] = useState('');

  const fetchAnalysts = () => {
    api.get('/analysts').then(res => setAnalystEmails(res.data.emails || []));
  };
  useEffect(() => { fetchAnalysts(); }, []);
  const addAnalyst = async () => {
    if (!newAnalyst.trim()) return;
    try {
      const res = await api.post('/analysts', { email: newAnalyst.trim() });
      setAnalystEmails(res.data.emails);
      setNewAnalyst('');
      setAnalystMsg(`Added ${newAnalyst.trim()}. Ask them to log in — they'll be routed to Client Intelligence.`);
      try { localStorage.setItem('wolf_analyst_emails', JSON.stringify(res.data.emails)); } catch (e) { /* noop */ }
      setTimeout(() => setAnalystMsg(''), 4000);
    } catch (err) {
      setAnalystMsg('Error: ' + (err.response?.data?.detail || err.message));
    }
  };
  const removeAnalyst = async (email) => {
    if (!window.confirm(`Remove ${email} from the analyst team?`)) return;
    const res = await api.delete(`/analysts/${encodeURIComponent(email)}`);
    setAnalystEmails(res.data.emails);
    try { localStorage.setItem('wolf_analyst_emails', JSON.stringify(res.data.emails)); } catch (e) { /* noop */ }
  };

  useEffect(() => {
    api.get('/companies').then(res => {
      setCompanies(res.data);
      const drafts = {};
      res.data.forEach(c => {
        drafts[c.id] = {
          full_name: c.full_name || '',
          gstin: c.gstin || '',
          address: c.address || '',
          bank_details: c.bank_details || '',
          phone: c.phone || '',
          email: c.email || '',
          email_alias: c.email_alias || '',
        };
      });
      setCompanyDrafts(drafts);
    });
    api.get('/settings/followup_webhook_url').then(res => {
      const v = res.data.value;
      setFollowupWebhook(v?.url || '');
    });
    api.get('/settings/followup_rules').then(res => {
      if (res.data.value) setFollowupRules(res.data.value);
    });
    api.get('/settings/followup_templates').then(res => {
      if (res.data.value) setFollowupTemplates(prev => ({ ...prev, ...res.data.value }));
    });
    api.get('/oauth/gmail/status').then(res => {
      setGmailStatus(res.data);
      if (res.data.connected) {
        api.get('/oauth/gmail/aliases')
          .then(r => setGmailAliases((r.data.aliases || []).filter(a => a.verified)))
          .catch(e => setAliasesError(e.response?.data?.detail || 'Could not load aliases'));
      }
    });
  }, []);

  // Surface OAuth callback result (deferred to avoid setState-in-effect lint)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('gmail');
    if (!status) return;
    const reason = params.get('reason');
    window.history.replaceState({}, '', window.location.pathname);
    const t = setTimeout(() => {
      if (status === 'connected') {
        setSavedMsg('Gmail connected successfully');
      } else if (status === 'error') {
        setSavedMsg('Gmail connection failed: ' + (reason || 'unknown'));
      }
    }, 0);
    const t2 = setTimeout(() => setSavedMsg(''), 3500);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, []);

  const connectGmail = async () => {
    const res = await api.get('/oauth/gmail/login');
    window.location.href = res.data.auth_url;
  };

  const disconnectGmail = async () => {
    if (!window.confirm('Disconnect Gmail? You will need to reconnect to send emails.')) return;
    await api.post('/oauth/gmail/disconnect');
    setGmailStatus({ connected: false });
    setSavedMsg('Gmail disconnected');
    setTimeout(() => setSavedMsg(''), 2500);
  };

  const updateCompanyDraft = (id, field, value) => {
    setCompanyDrafts(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveCompany = async (id) => {
    try {
      const res = await api.put(`/companies/${id}`, companyDrafts[id]);
      setCompanies(prev => prev.map(c => c.id === id ? res.data : c));
      setSavedMsg(`${res.data.full_name || res.data.name} saved`);
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (e) {
      setSavedMsg('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const saveSetting = async (key, value) => {
    await api.put(`/settings/${key}`, { value });
    setSavedMsg(`${key} saved`);
    setTimeout(() => setSavedMsg(''), 2500);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMsg('');
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setMsg('Error: ' + error.message);
    } else {
      setMsg('Password updated successfully');
      setPassword('');
    }
  };

  const toggleChannel = (idx, channel) => {
    const newSched = [...followupRules.schedules];
    const channels = newSched[idx].channels;
    if (channels.includes(channel)) {
      newSched[idx].channels = channels.filter(c => c !== channel);
    } else {
      newSched[idx].channels = [...channels, channel];
    }
    setFollowupRules({ ...followupRules, schedules: newSched });
  };

  const updateDays = (idx, days) => {
    const newSched = [...followupRules.schedules];
    newSched[idx].days = parseInt(days) || 0;
    setFollowupRules({ ...followupRules, schedules: newSched });
  };

  const runFollowupsNow = async () => {
    try {
      await api.post('/followups/run-now');
      setSavedMsg('Follow-up job triggered — check Audit Log');
      setTimeout(() => setSavedMsg(''), 3500);
    } catch (e) {
      setSavedMsg('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const inputCls = "w-full bg-transparent border border-border focus:border-maroon outline-none p-3 font-body text-sm transition-colors";
  const labelCls = "block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2";

  return (
    <Layout title="Settings">
      {savedMsg && (
        <div className="mb-4 p-3 bg-success/10 border border-success/30 text-success font-body text-sm">{savedMsg}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gmail OAuth Connection */}
        <div className="bg-surface border border-border p-6 fade-up">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">Email Channel</p>
          <h2 className="font-heading text-xl font-medium text-text-primary mb-2 flex items-center gap-2">
            <Mail className="w-5 h-5 text-maroon" strokeWidth={1.5} /> Gmail Connection
          </h2>
          <p className="font-body text-xs text-text-secondary mb-4">
            Send invoices and follow-up emails directly from your Gmail. Replace Make.com&apos;s email step. The PDF is attached and the sender uses the per-company alias defined below (e.g. <code>sales@wolfmediamarketing.com</code>).
          </p>

          {gmailStatus.connected ? (
            <div data-testid="gmail-status-connected" className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/30">
                <CheckCircle2 className="w-4 h-4 text-success" strokeWidth={1.5} />
                <div className="flex-1">
                  <p className="font-body text-sm text-text-primary">Connected as <strong>{gmailStatus.user_email || '—'}</strong></p>
                  {gmailStatus.connected_at && (
                    <p className="font-body text-xs text-text-secondary">Since {new Date(gmailStatus.connected_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
              <button data-testid="disconnect-gmail" onClick={disconnectGmail} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-4 py-2 font-heading text-sm flex items-center transition-colors">
                <XCircle className="w-4 h-4 mr-2" strokeWidth={1.5} /> Disconnect
              </button>
            </div>
          ) : (
            <div data-testid="gmail-status-disconnected" className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-border/30 border border-border">
                <XCircle className="w-4 h-4 text-text-secondary" strokeWidth={1.5} />
                <p className="font-body text-sm text-text-secondary">Not connected — emails will fail until you connect.</p>
              </div>
              <button data-testid="connect-gmail" onClick={connectGmail} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading text-sm flex items-center transition-colors">
                <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} /> Connect Gmail
              </button>
            </div>
          )}
        </div>

        {/* Follow-up Webhook (WhatsApp only) */}
        <div className="bg-surface border border-border p-6 fade-up fade-up-d1">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">Make.com Integration</p>
          <h2 className="font-heading text-xl font-medium text-text-primary mb-2">WhatsApp Follow-up Webhook</h2>
          <p className="font-body text-xs text-text-secondary mb-4">
            Used only for the <strong>WhatsApp</strong> channel of automated follow-ups (the Email channel uses Gmail directly — see above). Payload includes rendered body, days_overdue, client phone + balance.
          </p>
          <label className={labelCls}>Webhook URL</label>
          <input
            data-testid="followup-webhook-input"
            type="url"
            value={followupWebhook}
            onChange={(e) => setFollowupWebhook(e.target.value)}
            placeholder="https://hook.make.com/..."
            className={inputCls + " mb-3"}
          />
          <div className="flex gap-2">
            <button data-testid="save-followup-webhook" onClick={() => saveSetting('followup_webhook_url', { url: followupWebhook })} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading text-sm flex items-center transition-colors">
              <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save
            </button>
            <button data-testid="run-followups-now" onClick={runFollowupsNow} className="bg-transparent text-maroon border border-maroon hover:bg-cream px-5 py-2 font-heading text-sm flex items-center transition-colors">
              <Send className="w-4 h-4 mr-2" strokeWidth={1.5} /> Run Job Now
            </button>
          </div>
        </div>

        {/* Follow-up Schedule */}
        <div className="lg:col-span-2 bg-surface border border-border p-6 fade-up fade-up-d2">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">Automation Rules</p>
          <h2 className="font-heading text-xl font-medium text-text-primary mb-4">Follow-up Schedule</h2>
          <p className="font-body text-xs text-text-secondary mb-6">
            Trigger follow-ups at these days past due date. Each milestone is sent only once per channel per invoice. Daily check runs at 09:30.
          </p>
          
          <div className="space-y-3 mb-4">
            <div className="grid grid-cols-12 gap-3 pb-2 border-b border-border">
              <div className="col-span-3 font-body text-xs uppercase tracking-[0.2em] text-text-secondary">Days Overdue</div>
              <div className="col-span-7 font-body text-xs uppercase tracking-[0.2em] text-text-secondary">Channels</div>
              <div className="col-span-2"></div>
            </div>
            {followupRules.schedules.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-3">
                  <input
                    data-testid={`followup-days-${i}`}
                    type="number"
                    value={s.days}
                    onChange={(e) => updateDays(i, e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div className="col-span-7 flex gap-2">
                  {['email', 'whatsapp'].map(ch => (
                    <button
                      key={ch}
                      data-testid={`followup-${i}-${ch}`}
                      type="button"
                      onClick={() => toggleChannel(i, ch)}
                      className={`px-4 py-2 border text-xs uppercase tracking-widest font-body transition-colors ${s.channels.includes(ch) ? 'bg-maroon text-white border-maroon' : 'bg-transparent text-text-secondary border-border hover:border-maroon hover:text-maroon'}`}
                    >
                      {ch}
                    </button>
                  ))}
                </div>
                <div className="col-span-2 text-right">
                  <button onClick={() => setFollowupRules({ ...followupRules, schedules: followupRules.schedules.filter((_, idx) => idx !== i) })} className="font-body text-xs uppercase tracking-widest text-text-secondary hover:text-maroon">Remove</button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex gap-2 pt-4 border-t border-border">
            <button onClick={() => setFollowupRules({ ...followupRules, schedules: [...followupRules.schedules, { days: 0, channels: ['email'] }] })} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover">+ Add milestone</button>
            <div className="flex-1" />
            <button data-testid="save-followup-rules" onClick={() => saveSetting('followup_rules', followupRules)} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading text-sm flex items-center transition-colors">
              <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save Schedule
            </button>
          </div>
        </div>

        {/* Follow-up Templates */}
        <div className="lg:col-span-2 bg-surface border border-border p-6 fade-up fade-up-d2">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">Message Templates</p>
          <h2 className="font-heading text-xl font-medium text-text-primary mb-2">Follow-up Templates</h2>
          <p className="font-body text-xs text-text-secondary mb-5">
            Variables: <code className="text-maroon">{'{{client_name}}'}</code>, <code className="text-maroon">{'{{invoice_no}}'}</code>, <code className="text-maroon">{'{{invoice_date}}'}</code>, <code className="text-maroon">{'{{due_date}}'}</code>, <code className="text-maroon">{'{{days_overdue}}'}</code>, <code className="text-maroon">{'{{balance_due}}'}</code>, <code className="text-maroon">{'{{company_name}}'}</code>. The webhook payload includes both <code>template</code> (rendered) and <code>raw_template</code>.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div>
              <h3 className="font-heading text-sm font-medium text-text-primary mb-3 uppercase tracking-[0.15em]">Email</h3>
              <label className={labelCls}>Subject</label>
              <input
                data-testid="template-email-subject"
                type="text"
                value={followupTemplates.email_subject}
                onChange={(e) => setFollowupTemplates({ ...followupTemplates, email_subject: e.target.value })}
                className={inputCls + " mb-3"}
              />
              <label className={labelCls}>Body</label>
              <textarea
                data-testid="template-email-body"
                value={followupTemplates.email_body}
                onChange={(e) => setFollowupTemplates({ ...followupTemplates, email_body: e.target.value })}
                rows={8}
                className={inputCls + " font-mono"}
              />
            </div>
            <div>
              <h3 className="font-heading text-sm font-medium text-text-primary mb-3 uppercase tracking-[0.15em]">WhatsApp</h3>
              <label className={labelCls}>Message</label>
              <textarea
                data-testid="template-whatsapp-body"
                value={followupTemplates.whatsapp_body}
                onChange={(e) => setFollowupTemplates({ ...followupTemplates, whatsapp_body: e.target.value })}
                rows={11}
                className={inputCls + " font-mono"}
              />
            </div>
          </div>
          <div className="flex justify-end pt-5 mt-5 border-t border-border">
            <button data-testid="save-followup-templates" onClick={() => saveSetting('followup_templates', followupTemplates)} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading text-sm flex items-center transition-colors">
              <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save Templates
            </button>
          </div>
        </div>

        {/* Company Details (editable) */}
        <div className="lg:col-span-2 bg-surface border border-border p-6 fade-up fade-up-d3">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">Business Identity</p>
          <h2 className="font-heading text-xl font-medium text-text-primary mb-2">Company Details</h2>
          <p className="font-body text-xs text-text-secondary mb-6">
            These appear in the Invoice PDF footer (address on left, phone+email on right under &ldquo;Contact Us&rdquo;). Use <code>\n</code> or line breaks in Address to render multi-line (e.g. Reg off / Branch off).
          </p>
          <div className="space-y-8">
            {companies.map(c => {
              const draft = companyDrafts[c.id] || {};
              return (
                <div key={c.id} data-testid={`company-card-${c.name.toLowerCase()}`} className="border-b border-border pb-8 last:border-0">
                  <div className="flex items-baseline gap-3 mb-4">
                    <h3 className="font-heading text-lg text-text-primary font-medium">{c.full_name || c.name}</h3>
                    <span className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary">{c.name}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Full Name</label>
                      <input
                        data-testid={`company-${c.name.toLowerCase()}-full-name`}
                        type="text"
                        value={draft.full_name}
                        onChange={(e) => updateCompanyDraft(c.id, 'full_name', e.target.value)}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>GSTIN</label>
                      <input
                        data-testid={`company-${c.name.toLowerCase()}-gstin`}
                        type="text"
                        value={draft.gstin}
                        onChange={(e) => updateCompanyDraft(c.id, 'gstin', e.target.value)}
                        className={inputCls + " mono-num"}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Phone</label>
                      <input
                        data-testid={`company-${c.name.toLowerCase()}-phone`}
                        type="text"
                        value={draft.phone}
                        onChange={(e) => updateCompanyDraft(c.id, 'phone', e.target.value)}
                        className={inputCls + " mono-num"}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Email</label>
                      <input
                        data-testid={`company-${c.name.toLowerCase()}-email`}
                        type="email"
                        value={draft.email}
                        onChange={(e) => updateCompanyDraft(c.id, 'email', e.target.value)}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Gmail Send-as Alias</label>
                      {gmailAliases.length > 0 ? (
                        <select
                          data-testid={`company-${c.name.toLowerCase()}-email-alias`}
                          value={draft.email_alias}
                          onChange={(e) => updateCompanyDraft(c.id, 'email_alias', e.target.value)}
                          className={inputCls}
                        >
                          <option value="">Use default (connected Gmail)</option>
                          {gmailAliases.map(a => (
                            <option key={a.email} value={a.email}>
                              {a.email}{a.is_primary ? ' (primary)' : ''}{a.name ? ` — ${a.name}` : ''}
                            </option>
                          ))}
                          {/* If a saved alias is no longer in the verified list, still show it so the user sees what's stored */}
                          {draft.email_alias && !gmailAliases.some(a => a.email === draft.email_alias) && (
                            <option value={draft.email_alias}>{draft.email_alias} (not verified in Gmail)</option>
                          )}
                        </select>
                      ) : (
                        <input
                          data-testid={`company-${c.name.toLowerCase()}-email-alias`}
                          type="email"
                          value={draft.email_alias}
                          onChange={(e) => updateCompanyDraft(c.id, 'email_alias', e.target.value)}
                          placeholder={`sales@${c.name.toLowerCase()}marketing.com`}
                          className={inputCls}
                        />
                      )}
                      <p className="font-body text-[10px] text-text-secondary mt-1">
                        {gmailAliases.length > 0
                          ? `Pulled from Gmail (${gmailAliases.length} verified). Add more under Gmail → Settings → Accounts and Import → Send mail as.`
                          : aliasesError
                            ? `${aliasesError} — typing free-text still works.`
                            : 'Must be added & verified in Gmail → Settings → Accounts → Send mail as. Leave blank to use connected Gmail.'}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelCls}>Address (multi-line)</label>
                      <textarea
                        data-testid={`company-${c.name.toLowerCase()}-address`}
                        rows={3}
                        value={draft.address}
                        onChange={(e) => updateCompanyDraft(c.id, 'address', e.target.value)}
                        className={inputCls}
                        placeholder={"Reg off: ...\nBranch off: ..."}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelCls}>Bank Details (fallback when no bank accounts)</label>
                      <textarea
                        data-testid={`company-${c.name.toLowerCase()}-bank-details`}
                        rows={2}
                        value={draft.bank_details}
                        onChange={(e) => updateCompanyDraft(c.id, 'bank_details', e.target.value)}
                        className={inputCls}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <button data-testid={`save-company-${c.name.toLowerCase()}`} onClick={() => saveCompany(c.id)} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading text-sm flex items-center transition-colors">
                      <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save {c.name}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Password */}
        <div className="bg-surface border border-border p-6 fade-up fade-up-d3">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">Security</p>
          <h2 className="font-heading text-xl font-medium text-text-primary mb-6">Change Password</h2>
          <form onSubmit={handlePasswordChange}>
            <label className={labelCls}>New Password</label>
            <input
              data-testid="new-password-input"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls + " mb-4"}
            />
            {msg && <p className={`font-body text-sm mb-4 ${msg.startsWith('Error') ? 'text-maroon' : 'text-success'}`}>{msg}</p>}
            <button data-testid="update-password-button" type="submit" className="bg-maroon text-white hover:bg-maroon-hover px-6 py-3 font-heading text-sm transition-colors">Update Password</button>
          </form>
        </div>

        {/* Analyst Team Roster */}
        <div data-testid="analyst-team-section" className="bg-surface border border-border p-6 mt-8">
          <h2 className="font-heading text-xl font-light mb-2">Analyst Team</h2>
          <p className="font-body text-sm text-text-secondary mb-4">
            Users on this list get read-only access to <strong>Client Intelligence</strong>, <strong>Retainers</strong>, and <strong>Clients</strong> only.
            They will not see any invoices, payments, revenue, or expenses. Add their Gmail address after they sign up via the login page.
          </p>
          <div className="flex gap-2 mb-4">
            <input
              data-testid="new-analyst-email"
              type="email"
              value={newAnalyst}
              onChange={(e) => setNewAnalyst(e.target.value)}
              placeholder="analyst@wolfmediamarketing.com"
              className="flex-1 bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm"
            />
            <button
              data-testid="add-analyst-button"
              type="button"
              onClick={addAnalyst}
              className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading text-sm transition-colors"
            >
              Add
            </button>
          </div>
          {analystMsg && <p className={`font-body text-sm mb-4 ${analystMsg.startsWith('Error') ? 'text-maroon' : 'text-success'}`}>{analystMsg}</p>}
          {analystEmails.length === 0 ? (
            <p className="font-body text-sm text-text-secondary">No analysts added yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {analystEmails.map(email => (
                <li key={email} className="flex items-center justify-between py-2">
                  <span className="font-body text-sm text-text-primary">{email}</span>
                  <button
                    data-testid={`remove-analyst-${email}`}
                    onClick={() => removeAnalyst(email)}
                    className="font-body text-xs uppercase tracking-widest text-text-secondary hover:text-maroon"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
